import { z } from 'zod';

// --- Position ---------------------------------------------------------------

export const positionSchema = z.object({
    x: z.number(),
    y: z.number(),
});
export type Position = z.infer<typeof positionSchema>;

// --- Node types -------------------------------------------------------------
// MVP node set:
//   user-question   → text input from user (workflow entry)
//   document-upload → indexed files (stub in MVP, just metadata)
//   ollama-agent    → LLM call against local Ollama
//   output          → terminal node, displays text result
//   trigger-run     → run button source (acts as flow start)

export const nodeKindSchema = z.enum([
    'trigger-run',
    'user-question',
    // `document-upload` is kept in the enum for back-compat so OLD saved
    // workflows still parse, render, and run — it is no longer offered in the
    // palette (WS-B) nor advertised to the AI builder.
    'document-upload',
    // WS-B: read firm ontology records / run a named-agent read function.
    'ontology-source',
    'agent-function',
    'ollama-agent',
    'anthropic-agent',
    'output',
]);
export type NodeKind = z.infer<typeof nodeKindSchema>;

// --- Per-kind data schemas --------------------------------------------------

export const userQuestionDataSchema = z.object({
    label: z.string().default('User Question'),
    placeholder: z.string().default('Allow users to input text'),
    value: z.string().default(''),
});
export type UserQuestionData = z.infer<typeof userQuestionDataSchema>;

// A document is stored on disk; the node carries its metadata + stable id
// so the engine can resolve and read content at execution time.
export const documentRefSchema = z.union([
    // Back-compat: bare string (legacy seeds). Treated as a label only.
    z.string(),
    z.object({
        id: z.string(),
        name: z.string(),
        size: z.number().nonnegative().default(0),
        mime: z.string().default('text/plain'),
        excerpt: z.string().default('').optional(),
    }),
]);
export type DocumentRef = z.infer<typeof documentRefSchema>;

export const documentUploadDataSchema = z.object({
    label: z.string().default('Document Upload'),
    description: z.string().default('Index files and search over them'),
    documents: z.array(documentRefSchema).default([]),
    // Max characters of each document content to inject into the LLM prompt.
    maxContextChars: z.number().int().positive().default(4000),
});
export type DocumentUploadData = z.infer<typeof documentUploadDataSchema>;

// An ontology-source node reads firm records through the server's `ctx.read`
// (persona-bounded). The node carries WHICH Object Type to read, an optional
// `where` filter, a row cap, and an optional field projection. The engine never
// touches the ontology itself — it routes the read through the injected runner.
export const ontologySourceDataSchema = z.object({
    label: z.string().default('Data Source'),
    objectTypeKey: z.string(),
    filter: z.record(z.string(), z.unknown()).default({}),
    maxRows: z.number().int().positive().default(50),
    fields: z.array(z.string()).default([]),
});
export type OntologySourceData = z.infer<typeof ontologySourceDataSchema>;

// An agent-function node runs a named, READ-ONLY function declared on an agent
// (e.g. MASON `list_recon_breaks`). The node carries the agent code + function
// key; the server's catalog resolves it and serialises the read to text.
export const agentFunctionDataSchema = z.object({
    label: z.string().default('Agent Function'),
    agentCode: z.string(),
    functionKey: z.string(),
    params: z.record(z.string(), z.unknown()).default({}),
});
export type AgentFunctionData = z.infer<typeof agentFunctionDataSchema>;

export const ollamaAgentDataSchema = z.object({
    label: z.string().default('Ollama Agent'),
    model: z.string().default('qwen3.6:latest'),
    systemPrompt: z.string().default('You are a helpful assistant.'),
    temperature: z.number().min(0).max(2).default(0.7),
    // Where the Ollama server lives. Defaults to the API server's own
    // OLLAMA_URL env (localhost in dev). Set to a tunnel / LAN URL when
    // the backend runs in the cloud and you want it to call your machine.
    endpointUrl: z.string().default(''),
});
export type OllamaAgentData = z.infer<typeof ollamaAgentDataSchema>;

export const anthropicAgentDataSchema = z.object({
    label: z.string().default('Custom Agent'),
    model: z.string().default('claude-sonnet-4-6'),
    systemPrompt: z.string().default('You are a helpful assistant.'),
    maxTokens: z.number().int().positive().default(4096),
});
export type AnthropicAgentData = z.infer<typeof anthropicAgentDataSchema>;

export const outputDataSchema = z.object({
    label: z.string().default('Output'),
    description: z.string().default('Output the results of nodes as text'),
    format: z.enum(['text', 'formatted']).default('text'),
});
export type OutputData = z.infer<typeof outputDataSchema>;

export const triggerRunDataSchema = z.object({
    label: z.string().default('Run'),
});
export type TriggerRunData = z.infer<typeof triggerRunDataSchema>;

// --- Discriminated node union ----------------------------------------------

export const baseNodeSchema = z.object({
    id: z.string(),
    position: positionSchema,
});

export const workflowNodeSchema = z.discriminatedUnion('kind', [
    baseNodeSchema.extend({ kind: z.literal('trigger-run'), data: triggerRunDataSchema }),
    baseNodeSchema.extend({ kind: z.literal('user-question'), data: userQuestionDataSchema }),
    baseNodeSchema.extend({ kind: z.literal('document-upload'), data: documentUploadDataSchema }),
    baseNodeSchema.extend({ kind: z.literal('ontology-source'), data: ontologySourceDataSchema }),
    baseNodeSchema.extend({ kind: z.literal('agent-function'), data: agentFunctionDataSchema }),
    baseNodeSchema.extend({ kind: z.literal('ollama-agent'), data: ollamaAgentDataSchema }),
    baseNodeSchema.extend({ kind: z.literal('anthropic-agent'), data: anthropicAgentDataSchema }),
    baseNodeSchema.extend({ kind: z.literal('output'), data: outputDataSchema }),
]);
export type WorkflowNode = z.infer<typeof workflowNodeSchema>;

// --- Edges ------------------------------------------------------------------

export const workflowEdgeSchema = z.object({
    id: z.string(),
    source: z.string(),
    target: z.string(),
});
export type WorkflowEdge = z.infer<typeof workflowEdgeSchema>;

// --- Workflow ---------------------------------------------------------------

export const workflowSchema = z.object({
    id: z.string(),
    name: z.string(),
    version: z.literal(1).default(1),
    nodes: z.array(workflowNodeSchema),
    edges: z.array(workflowEdgeSchema),
    createdAt: z.string().datetime().optional(),
    updatedAt: z.string().datetime().optional(),
});
export type Workflow = z.infer<typeof workflowSchema>;
